import type { TicketOrderListItem } from '@pulsify/shared/dto'
import { Copy, Eye, MoreHorizontal, RotateCcw, TicketX } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import type { TicketAttendee } from '~/entities/ticket/api/tickets-api'
import { ticketsApi } from '~/entities/ticket/api/tickets-api'
import { formatMoney } from '~/entities/ticket/lib/ticket-earnings'
import { useRefundTicketOrder, useVoidTicket } from '~/entities/ticket/model/use-tickets'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { copyToClipboard } from '~/shared/lib/share'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Button, buttonVariants } from '~/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/shared/ui/dropdown-menu'
import { openDocument } from '../lib/open-document'
import { activeTickets } from '../lib/ticket-figures'

type Order = TicketOrderListItem

export type OrderChange =
  | { kind: 'refund'; orderId: string }
  | { kind: 'void'; orderId: string; ticketId: string }

type Pending =
  | { kind: 'refund'; order: Order }
  | { kind: 'void'; order: Order; ticket: TicketAttendee }

export interface OrderActions {
  openInvoice: (order: Order) => void
  askRefund: (order: Order) => void
  askVoid: (order: Order, ticket: TicketAttendee) => void
  busy: boolean
  dialog: { open: boolean; pending: Pending | null; close: () => void; confirm: () => void }
}

export function useOrderActions(
  businessId: string,
  eventId: string,
  onChange: (change: OrderChange) => void,
): OrderActions {
  const refund = useRefundTicketOrder(businessId, eventId)
  const voidTicket = useVoidTicket(businessId, eventId)
  const [pending, setPending] = useState<Pending | null>(null)
  const [open, setOpen] = useState(false)
  const ask = (next: Pending) => {
    setPending(next)
    setOpen(true)
  }
  const onError = (error: unknown) => toast.error(getErrorMessageFromError(error))

  const confirm = () => {
    setOpen(false)
    if (!pending) return
    if (pending.kind === 'refund') {
      refund.mutate(pending.order.id, {
        onSuccess: () => {
          toast.success(m.ticketsRefunded())
          onChange({ kind: 'refund', orderId: pending.order.id })
        },
        onError,
      })
      return
    }
    voidTicket.mutate(pending.ticket.id, {
      onSuccess: () => {
        toast.success(m.ticketsVoided())
        onChange({ kind: 'void', orderId: pending.order.id, ticketId: pending.ticket.id })
      },
      onError,
    })
  }

  return {
    openInvoice: (order) => openDocument(() => ticketsApi.orderInvoiceLink(businessId, order.id)),
    askRefund: (order) => ask({ kind: 'refund', order }),
    askVoid: (order, ticket) => ask({ kind: 'void', order, ticket }),
    busy: refund.isPending || voidTicket.isPending,
    dialog: { open, pending, close: () => setOpen(false), confirm },
  }
}

export function OrderActionDialog({
  dialog,
  locale,
}: {
  dialog: OrderActions['dialog']
  locale: string
}) {
  const { pending } = dialog
  const name = pending?.order.buyerName ?? m.ticketsNoName()
  const title =
    pending?.kind === 'void'
      ? m.ticketsVoidTitle({ reference: pending.ticket.reference })
      : m.ticketsRefundTitle()
  const body =
    pending?.kind === 'void'
      ? m.ticketsVoidBody()
      : m.ticketsRefundBody({
          name,
          amount: pending
            ? formatMoney(pending.order.totalCents, pending.order.currency, locale)
            : '',
        })

  return (
    <AlertDialog open={dialog.open} onOpenChange={(open) => (open ? null : dialog.close())}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: 'destructive' })}
            onClick={dialog.confirm}
          >
            {pending?.kind === 'void' ? m.ticketsVoid() : m.ticketsRefund()}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export async function copyEmail(email: string) {
  if (await copyToClipboard(email)) toast.success(m.ticketPageEmailCopied())
  else toast.error(m.ticketPageCopyFailed())
}

export function OrderRowMenu({
  order,
  actions,
  onOpen,
}: {
  order: Order
  actions: OrderActions
  onOpen: () => void
}) {
  const valid = activeTickets(order)
  const name = order.buyerName ?? m.ticketsNoName()
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={m.ticketPageActions({ name })}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuItem onSelect={onOpen}>
          <Eye />
          {m.ticketPageShowDetails()}
        </DropdownMenuItem>
        {order.buyerEmail ? (
          <DropdownMenuItem onSelect={() => copyEmail(order.buyerEmail ?? '')}>
            <Copy />
            {m.ticketPageCopyEmail()}
          </DropdownMenuItem>
        ) : null}
        {order.status === 'paid' || valid.length > 0 ? <DropdownMenuSeparator /> : null}
        {valid.length === 1 ? (
          <DropdownMenuItem
            variant="destructive"
            disabled={actions.busy}
            onSelect={() => actions.askVoid(order, valid[0])}
          >
            <TicketX />
            {m.ticketPageVoidTicket()}
          </DropdownMenuItem>
        ) : null}
        {valid.length > 1 ? (
          <DropdownMenuItem onSelect={onOpen}>
            <TicketX />
            {m.ticketPageVoidSome()}
          </DropdownMenuItem>
        ) : null}
        {order.status === 'paid' ? (
          <DropdownMenuItem
            variant="destructive"
            disabled={actions.busy}
            onSelect={() => actions.askRefund(order)}
          >
            <RotateCcw />
            {m.ticketsRefund()}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
